<template>
  <a-drawer
    placement="left"
    id="sidebar-wrap"
    :closable="false"
    :visible="drawerVisible"
    :z-index="98"
    :width="200"
    :body-style="{ paddingTop: '60px', paddingLeft: 0, paddingRight: 0, paddingBottom: 0, height: '100%', overflow: 'visible' }"
    @close="handleClose">
    <div class="sidebar-wrap" :style="wrapStyle">
      <products-list
        :active-menu="activeMenu"
        :menus="menus"
        :stared-menus="staredMenus"
        :products-panel-open="productsPanelOpen"
        @close="handleClose"
        @activeMenuChange="handleActiveMenuChange"
        @scroll-to-section="handleScrollToSection"
        @updateStaredMenu="handleUpdateStaredMenu"
        @routerChange="handleRouterChange"
        @products-panel-open-change="handleProductsPanelOpenChange"
        @cascade-open-change="handleCascadeOpenChange" />
      <products-sub-list
        ref="productsSubList"
        class="products-sub-panel"
        :class="{ 'is-open': productsPanelOpen }"
        :menus="menus"
        :active-menu="activeMenu"
        :cascade-menu-open="cascadeMenuOpen"
        :panel-style="productsPanelStyle"
        @mouseenter.native="handleProductsPanelEnter"
        @mouseleave.native="handleProductsPanelLeave"
        @close="handleClose"
        @updateStaredMenu="handleUpdateStaredMenu"
        @routerChange="handleRouterChange" />
    </div>
  </a-drawer>
</template>

<script>
import get from 'lodash/get'
import { mapGetters, mapState } from 'vuex'
import ProductsList from '@scope/sections/ProductsList'
import ProductsSubList from '@scope/sections/ProductsSubList'

const PRODUCTS_PANEL_CLOSE_DELAY = 200
const PRODUCTS_PANEL_WIDTH = 800

export default {
  name: 'Sidebar',
  components: {
    ProductsList,
    ProductsSubList,
  },
  props: {
    menus: Array,
    staredMenus: Array,
  },
  data () {
    return {
      activeMenu: { index: null },
      cascadeMenuOpen: false,
      productsPanelOpen: false,
      productsPanelCloseTimer: null,
    }
  },
  computed: {
    ...mapGetters(['common']),
    ...mapState('common', ['openCloudShell', 'cloudShellHeight']),
    wrapStyle () {
      return {
        height: this.openCloudShell ? `calc(100% - ${this.cloudShellHeight}px)` : '100%',
      }
    },
    drawerVisible () {
      return get(this.common, 'sidebar.drawerVisible', false)
    },
    productsPanelStyle () {
      const style = {
        width: `${PRODUCTS_PANEL_WIDTH}px`,
        top: '60px',
        left: '200px',
        bottom: this.openCloudShell ? `${this.cloudShellHeight}px` : '0',
      }
      return style
    },
  },
  watch: {
    drawerVisible (val) {
      if (!val) {
        this.cascadeMenuOpen = false
        this.productsPanelOpen = false
        this.clearProductsPanelTimer()
      }
    },
    productsPanelOpen (val) {
      if (val) {
        this.$nextTick(() => {
          const subList = this.$refs.productsSubList
          if (subList) {
            subList.refreshPadding()
          }
        })
      }
    },
  },
  beforeDestroy () {
    this.clearProductsPanelTimer()
  },
  methods: {
    clearProductsPanelTimer () {
      if (this.productsPanelCloseTimer) {
        clearTimeout(this.productsPanelCloseTimer)
        this.productsPanelCloseTimer = null
      }
    },
    openProductsPanel () {
      this.clearProductsPanelTimer()
      this.productsPanelOpen = true
    },
    scheduleCloseProductsPanel () {
      this.clearProductsPanelTimer()
      this.productsPanelCloseTimer = setTimeout(() => {
        this.productsPanelCloseTimer = null
        this.productsPanelOpen = false
      }, PRODUCTS_PANEL_CLOSE_DELAY)
    },
    handleProductsPanelOpenChange (open) {
      if (open) {
        this.openProductsPanel()
      } else {
        this.scheduleCloseProductsPanel()
      }
    },
    handleProductsPanelEnter () {
      this.openProductsPanel()
    },
    handleProductsPanelLeave () {
      this.scheduleCloseProductsPanel()
    },
    handleActiveMenuChange (menu) {
      this.activeMenu = menu
      if (menu && menu.index && menu.index !== 1) {
        this.openProductsPanel()
      }
    },
    handleScrollToSection (payload) {
      this.openProductsPanel()
      this.$nextTick(() => {
        const subList = this.$refs.productsSubList
        if (subList) {
          subList.scrollToSection(payload)
        }
      })
    },
    handleClose () {
      this.$store.dispatch('common/updateObject', {
        name: 'sidebar',
        data: {
          drawerVisible: false,
        },
      })
      this.$bus.$emit('ProductsSubDrawerClose')
    },
    handleUpdateStaredMenu (staredList) {
      this.$store.dispatch('common/updateObject', {
        name: 'sidebar',
        data: {
          staredList,
        },
      })
      this.$store.dispatch('profile/update', {
        staredList,
      })
    },
    handleRouterChange (menu) {
      this.$emit('routerChange', menu)
    },
    handleCascadeOpenChange (open) {
      this.cascadeMenuOpen = open
      if (open) {
        this.clearProductsPanelTimer()
        this.productsPanelOpen = false
      }
    },
  },
}
</script>

<style lang="less" scoped>
.sidebar-wrap {
  width: 200px;
  height: 100%;
  position: relative;
  overflow: visible;
}
</style>

<style lang="less">
/* 与二级/三级菜单一致：opacity + visibility 淡入淡出 */
.products-sub-panel.products-sub-root {
  position: fixed;
  z-index: 99;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.1s ease, visibility 0.1s ease;
  background: #fff;
  box-shadow: 1px 0 6px 0 rgba(165, 192, 207, 0.3);

  &.is-open {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }
}

/* 抽屉内容允许产品面板溢出显示 */
#sidebar-wrap.ant-drawer .ant-drawer-content-wrapper,
#sidebar-wrap.ant-drawer .ant-drawer-content,
#sidebar-wrap.ant-drawer .ant-drawer-wrapper-body,
#sidebar-wrap.ant-drawer .ant-drawer-body {
  overflow: visible !important;
}
</style>
